/* eslint-disable max-len */
// src/components/contentHub/ContentPlanner.js

import React, {useState, useCallback, useEffect} from "react";
import PropTypes from "prop-types";
import { FiCheckCircle, FiList, FiLoader, FiRefreshCw, FiAlertTriangle, FiArrowLeft, FiLayers } from "react-icons/fi";
import {httpsCallable} from "firebase/functions";
import {functions} from "../../firebaseConfig";
import { useToast } from '../shared/contexts/ToastContext';

const regeneratePostSubjectsFn = httpsCallable(functions, "regeneratePostSubjects");

function ContentPlanner({ 
    project, 
    contentPlan, 
    postSubjects, 
    onPlanConfirmed, 
    loading,
    onBack, // NEW
    workflowConfig // NEW
}) {
    const { addToast } = useToast();
    const [editableSubjects, setEditableSubjects] = useState([]);
    const [regenerationState, setRegenerationState] = useState({ all: false, indices: {} });
    const [error, setError] = useState("");

    useEffect(() => { if (Array.isArray(postSubjects)) { setEditableSubjects(postSubjects); } }, [postSubjects]);

    const isPlanValid = contentPlan && contentPlan.refinedTopic && contentPlan.angle && contentPlan.suggestedCTA;
    const subjectsExpected = contentPlan?.settings?.contentType === "fixed" || contentPlan?.settings?.contentType === "recurring";
    const areSubjectsPresent = Array.isArray(editableSubjects) && editableSubjects.length > 0 && editableSubjects.every((s) => s && s.trim().length > 0);
    const isRegenerating = regenerationState.all || Object.values(regenerationState.indices).some(Boolean);
    const canConfirmPlan = isPlanValid && (!subjectsExpected || areSubjectsPresent) && !loading && !isRegenerating;

    const handleSubjectChange = useCallback((index, value) => {
        setEditableSubjects((prev) => {
            const newSubjects = [...prev];
            newSubjects[index] = value;
            return newSubjects;
        });
    }, []);

    const handleRegenerate = useCallback(async (index = null) => {
        if (!contentPlan?.contentPlanId) {
            addToast('Content plan not loaded yet. Please wait.', 'error');
            return;
        }
        setError("");
        const isSingleRegen = index !== null;
        const subjectsToAvoid = isSingleRegen ? editableSubjects.filter((_, i) => i !== index) : editableSubjects;

        setRegenerationState((prev) => ({ ...prev, all: !isSingleRegen, indices: isSingleRegen ? { ...prev.indices, [index]: true } : {} }));

        try {
            const result = await regeneratePostSubjectsFn({
                projectId: project.id,
                contentPlanId: contentPlan.contentPlanId,
                subjectsToAvoid: subjectsToAvoid,
                indexToRegenerate: isSingleRegen ? index : undefined,
            });

            if (result.data.success && Array.isArray(result.data.newSubjects)) {
                setEditableSubjects(result.data.newSubjects);
            } else {
                throw new Error(result.data.error || "Failed to regenerate subjects.");
            }
        } catch (err) {
            setError("Regeneration failed. Please try again.");
            addToast('Failed to regenerate subjects.', 'error');
        } finally {
            setRegenerationState((prev) => ({ ...prev, all: false, indices: isSingleRegen ? { ...prev.indices, [index]: false } : {} }));
        }
    }, [project?.id, contentPlan?.contentPlanId, editableSubjects, addToast]);

    const handleConfirmClick = useCallback(() => {
        if (!canConfirmPlan) return;
        onPlanConfirmed(editableSubjects);
    }, [canConfirmPlan, onPlanConfirmed, editableSubjects]);

    // Context Badge Helper
    const platforms = workflowConfig?.platforms || (project.platform ? [project.platform] : []);
    const platformLabel = platforms.filter(Boolean).map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' & ');

    // Adjustments Summary Helper
    const adjustments = contentPlan?.settings?.adjustments || {};
    const summaryText = [
        adjustments.template && `Template: ${adjustments.template}`,
        adjustments.visualStyle && `Style: ${adjustments.visualStyle}`,
        adjustments.tone && `Tone: ${adjustments.tone}`
    ].filter(Boolean).join(' • ');

    if (!contentPlan && !loading) return (<div className="text-center p-6 text-gray-600"><p>Content plan data is not available.</p></div>);

    return (
        <div className="bg-white/5 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/10 relative overflow-hidden">
            
            {/* Header */}
            <div className="p-8 border-b border-white/10 bg-white/5 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center">
                    <button 
                        onClick={onBack}
                        className="mr-6 p-3 text-cream/40 hover:text-white rounded-full hover:bg-white/10 transition-colors"
                        title="Back to Adjustments"
                    >
                        <FiArrowLeft className="h-6 w-6" />
                    </button>
                    <div>
                        <h2 className="text-2xl font-bold text-white tracking-tight">The Plan</h2>
                        <p className="text-sm text-cream/60 mt-1 font-medium">Review the AI's strategy before writing.</p>
                    </div>
                </div>

                <div className="flex items-center px-4 py-2 bg-slate-blue/10 border border-slate-blue/30 rounded-full shadow-[0_0_15px_rgba(99,102,241,0.2)]">
                    <FiLayers className="text-slate-blue mr-2 h-4 w-4" />
                    <span className="text-xs font-bold text-cream/60 uppercase tracking-widest">
                        Creating for: <span className="text-slate-blue ml-1">{platformLabel}</span>
                    </span>
                </div>
            </div>

            <div className="p-8 space-y-8">
                
                {/* Adjustments Summary */}
                {summaryText && (
                    <div className="text-xs font-bold text-cream/40 px-4 py-3 bg-white/5 rounded-xl border border-white/10 inline-block tracking-wide">
                        <span className="text-cream mr-2 uppercase">Settings:</span> {summaryText}
                    </div>
                )}

                {/* The Plan */}
                <div className="space-y-4">
                    <h3 className="text-sm font-black text-white uppercase tracking-widest">Strategy Blueprint</h3>
                    {isPlanValid ? (
                        <div className="relative group">
                            <div className="absolute -inset-0.5 bg-gradient-to-r from-slate-blue to-rosy-taupe rounded-2xl blur opacity-20 group-hover:opacity-40 transition duration-1000"></div>
                            <div className="relative bg-warm-dark/80 border border-white/10 rounded-2xl p-6 space-y-4 backdrop-blur-sm">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    <div className="space-y-1">
                                        <p className="text-[10px] font-black text-slate-blue uppercase tracking-widest">Topic</p>
                                        <p className="text-cream font-medium leading-relaxed">{contentPlan.refinedTopic}</p>
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-[10px] font-black text-rosy-taupe uppercase tracking-widest">Angle</p>
                                        <p className="text-cream font-medium leading-relaxed">{contentPlan.angle}</p>
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-[10px] font-black text-pastel-green uppercase tracking-widest">Call to Action</p>
                                        <p className="text-cream font-medium leading-relaxed">{contentPlan.suggestedCTA}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (<p className="text-sm text-soft-coral italic font-medium border border-soft-coral/30 bg-soft-coral/10 p-4 rounded-xl">Failed to generate a complete content plan.</p>)}
                </div>

                {/* Subjects List */}
                {subjectsExpected && (
                    <div className="space-y-6 pt-8 border-t border-white/10">
                        <div className="flex justify-between items-center">
                            <h3 className="text-sm font-black text-white uppercase tracking-widest flex items-center"><FiList className="mr-2 text-slate-blue" /> Post Subjects ({editableSubjects.length})</h3>
                            <button onClick={() => handleRegenerate(null)} disabled={loading || isRegenerating} className="inline-flex items-center px-4 py-2 border border-white/10 rounded-xl text-xs font-bold text-cream bg-white/5 hover:bg-white/10 hover:border-white/20 hover:text-white transition-all disabled:opacity-50">
                                {regenerationState.all ? (<FiLoader className="mr-2 h-3 w-3 animate-spin" />) : (<FiRefreshCw className="mr-2 h-3 w-3" />)}
                                Regenerate All
                            </button>
                        </div>
                        {areSubjectsPresent ? (
                            <div className="space-y-3">
                                {editableSubjects.map((subject, index) => (
                                    <div key={index} className="flex items-center gap-4 group p-1 transition-all">
                                        <span className="text-cream/20 font-mono text-xs font-bold w-6 text-right">{(index + 1).toString().padStart(2, '0')}.</span>
                                        <div className="relative flex-grow">
                                             <input 
                                                type="text" 
                                                value={subject} 
                                                onChange={(e) => handleSubjectChange(index, e.target.value)} 
                                                className="w-full bg-white/5 border border-white/10 text-cream text-sm rounded-xl px-4 py-3 focus:outline-none focus:border-slate-blue/50 focus:ring-1 focus:ring-slate-blue/50 focus:bg-white/10 transition-all placeholder-white/20"
                                                disabled={loading || isRegenerating}
                                            />
                                        </div>
                                        <button onClick={() => handleRegenerate(index)} disabled={loading || isRegenerating} className="p-3 text-cream/20 hover:text-slate-blue hover:bg-slate-blue/10 rounded-xl transition-all disabled:opacity-0" title="Regenerate this subject">
                                            {regenerationState.indices[index] ? <FiLoader className="h-4 w-4 animate-spin" /> : <FiRefreshCw className="h-4 w-4" />}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        ) : (<p className="text-sm text-soft-coral italic">No specific post subjects were generated.</p>)}
                    </div>
                )}

                {error && (<div className="text-sm text-soft-coral font-bold flex items-center p-4 bg-soft-coral/10 border border-soft-coral/20 rounded-xl"><FiAlertTriangle className="mr-2 flex-shrink-0" />{error}</div>)}

                {/* Footer */}
                <div className="p-8 border-t border-white/10 flex justify-end bg-warm-dark/20 backdrop-blur-sm -mx-8 -my-8 mt-4 rounded-b-3xl">
                    <button 
                        onClick={handleConfirmClick} 
                        disabled={!canConfirmPlan} 
                        className={`w-full sm:w-auto inline-flex items-center justify-center px-8 py-4 border border-transparent text-sm font-bold rounded-2xl shadow-lg text-warm-dark bg-slate-blue hover:bg-slate-blue/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-blue/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-[1.02] active:scale-95`}
                    >
                        {loading || isRegenerating ? (<FiLoader className="mr-2 h-5 w-5 animate-spin" />) : (<FiCheckCircle className="mr-2 h-5 w-5" />)}
                        {loading || isRegenerating ? "Processing..." : "Generate Content"}
                    </button>
                </div>
            </div>
        </div>
    );
}

ContentPlanner.propTypes = {
    project: PropTypes.object.isRequired,
    contentPlan: PropTypes.object,
    postSubjects: PropTypes.arrayOf(PropTypes.string).isRequired,
    onPlanConfirmed: PropTypes.func.isRequired,
    loading: PropTypes.bool.isRequired,
    onBack: PropTypes.func.isRequired, // Required
    workflowConfig: PropTypes.object // Optional
};

export default ContentPlanner;
